import { ReactComponent as IconDataExchange } from '@/assets/svgs/data-element-icon.svg';
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconDataExchangeError } from '@/assets/svgs/icon-status-error.svg';
import { useEffect, useState } from "react";
import useModal from '@/hooks/use-modal';
import Modal from '@/componentsDS/Modal/Modal';
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Button from "@/componentsDS/Button/Button";
import { ButtonType , ButtonVariant} from '@/componentsDS/Button/Button.type';
import useServiceProviderRelationship from "@/hooks/use-service-provider-relation/index";
import ServiceProviderTablePage from "./components/ServiceProviderTable/index";
import useDataExchangeRelation from "@/hooks/use-data-exchange-relation/index";
import api from '@/services/api/serviceProviderRelation';
import Toast from '@/components/Toast';
import useToast from '@/hooks/use-toast';
import { useSelector } from 'react-redux';
import InputDropdown from '@/dsComponents/InputDropdownSelect/index';
import { filterCommonSpRecords, groupByCombination, isLastSpForAnyCombination } from './helpers/index';
import Portal from '@/components/Portal/index';

export const renderAPIAction = (action) => {
	switch (action) {
		case "APPROVED":
			return { apiPath: api.serviceProviderRelationsApprove, message: "Solution Provider relationship request approved" };
		case "REJECTED":
			return { apiPath: api.serviceProviderRelationsReject, message: "Solution Provider relationship request rejected" };
		case "REVOKED":
			return { apiPath: api.serviceProviderRelationsRevoke, message: "Solution Provider relationship request revoked" };
		case "CANCELLED":
		case "LAST_CANCELLED":
			return { apiPath: api.serviceProviderRelationsCancel, message: "Solution Provider relationship request cancelled" };
		case "LAST_REVOKED":
			return { apiPath: api.serviceProviderRelationsRevoke, message: "Solution Provider relationship request revoked" };
	}
}

const ServiceProviderPage = ({
	defaultSelectedAction = null
}) => {

	// CONSTANTS
	const title = "My Solution Provider";
	const description = "Manage the Solution Provider Relationships to enable them to provide and consume on your behalf.";
	const groupBy = "dataElementName";
	const emptyMessage = "No Solution Provider Relationship created yet.";

	// STATES
	const [refreshData, setRefreshData] = useState(false);
	const [selectedAction, setSelectedAction] = useState(defaultSelectedAction);
	const [selectedData, setSelectedData] = useState(null);
	const [selectedAllData, setSelectedAllData] = useState(null);

	// HOOKS
	const { visible: initiateActionModal, show: showActionModal, hide: hideActionModal } = useModal();
	const { loading: serviceProviderLoading,  fetchServiceProviderData, statusEnum, serviceProviderData, serviceProviderHeader, serviceProviderError } = useServiceProviderRelationship(handleAction)
	const {
		fetchOrgData,
		allOrgData,
		fetchDataElements,
		dataElement ,
		fetchPitstops,
		pitstops,
		fetchDataExchange,
		orgDataLoading,
		dataElementLoading,
		pitstopsLoading
	} = useDataExchangeRelation();

	// Get Profile
	const profile = useSelector(state => state.auth.profile);
	const orgId = profile?.organizationId;

	// Toast
	const [loading, setLoading] = useState(false);
	const toastType = {
		success: "success",
		error: "error"
	}
	const { toastList, addToastMessage } = useToast();

	// Fetch details on page load
	useEffect(() => {
		fetchServiceProviderData(`initiator=DATA_OWNER`, orgId);
	}, [refreshData]);

	useEffect(() => {
		if (serviceProviderError) {
			addToastMessage(serviceProviderError?.message, toastType.error);
		}
	}, [serviceProviderError]);

	useEffect(() => {
		fetchDataElements();
		fetchOrgData();
		fetchPitstops()
	}, []);

	// put them somewhere else later:


	const handleLastCancelRevoke  =async  (data, action)=>{
		const deRecords =await fetchDataExchange();
		let spId = data.serviceProviderOrgId ?? data.serviceProviderEmail;
		const filterObject ={
			dataElementId:data.dataElementId,
			spId,
			flow: data.flow
		}
		const filteredRecords =filterCommonSpRecords(deRecords, filterObject, orgId)
		const groupedRecords=groupByCombination(filteredRecords);
		const isLast = isLastSpForAnyCombination(groupedRecords, spId);

		if(isLast){
			setSelectedAction(`LAST_${action}`);
			setSelectedData(data);
			showActionModal();

		} else {
		setSelectedAction(action);
		setSelectedData(data);
		showActionModal();
		}

	}

	function handleAction(data, action) {
		if(action ==="CANCELLED" || action ==="REVOKED"){
			handleLastCancelRevoke(data, action);
		}else {

			setSelectedAction(action);
			setSelectedData(data);
			showActionModal();
		}
	}

	const [selectPitstops, setSelectPitstops] = useState([]);
	const [dropdownSearch, setDropdownSearch] = useState({
		serviceProviderSearch: '',
		pitstopSearch: ''
	})

	const pitstopDropdownSelector =(pitstop) =>{
		// Keep the picked option (array): InputDropdown reads dropdownSelectedOption as a list.
		setSelectPitstops(pitstop)
	}
	const dropdownSearchHandler = (e) => {
		setDropdownSearch({ ...dropdownSearch, "pitstopSearch": e });
	}

	function handleActionAll(data, type) {
		setSelectedAction(type);
		setSelectedAllData(data);
		showActionModal();
	}

	async function handleActionAllSubmit() {
		if (selectedAllData) {
			for (const data of selectedAllData) {
				await handleActionSubmit(data);
			}
			return setRefreshData(!refreshData);
		}
	}

	async function handleActionSubmit(selectedData) {
		setLoading(true);
		let payload = {};
		const {
			serviceProviderRelationshipId,
			serviceProviderRelationshipInvitesId
		} = selectedData;

		payload = {
			...(serviceProviderRelationshipId && { serviceProviderRelationshipId: serviceProviderRelationshipId }),
			...(serviceProviderRelationshipInvitesId && { serviceProviderRelationshipInvitesId: serviceProviderRelationshipInvitesId }),
		};

		if(selectPitstops.length>0){
			payload = {
				...payload,
				systemId: selectPitstops[0].id
			}
		}

		const { apiPath, message } = renderAPIAction(selectedAction);
		const [error, response] = await apiPath(payload);

		setLoading(false);
		if (error) {
			return addToastMessage(error?.message, toastType.error);
		}

		addToastMessage(response?.message ?? message, toastType.success);
		hideActionModal();
		return setRefreshData(!refreshData);
	}

	const renderButton = (action) => {
		switch (action) {
			case "APPROVED":
				return <Button
					variant={ButtonVariant.STANDARD}
					type={ButtonType.SOLID}
					text="Approve"
					data-testid="approve"
					onClick={() => {
						selectedData ? handleActionSubmit(selectedData) : handleActionAllSubmit()
					}}
					id="my-service-provider-modal-approve-button"
				/>
			case "REJECTED":
				return <Button
					variant={ButtonVariant.DESTRUCTIVE}
					type={ButtonType.SOLID}
					text="Reject"
					data-testid="reject"
					onClick={() => {
						selectedData ? handleActionSubmit(selectedData) : handleActionAllSubmit()
					}}
					id="my-service-provider-modal-reject-button"
				/>
			case "REVOKED":
				return <Button
					text="Revoke"
					variant={ButtonVariant.DESTRUCTIVE}
					type={ButtonType.SOLID}
					round
					warning
					loading={loading}
					data-testid="revoke"
					onClick={() => {
						selectedData ? handleActionSubmit(selectedData) : handleActionAllSubmit()
					}}
					id="my-service-provider-modal-revoke-button"
				/>

			case "CANCELLED":
			case "LAST_CANCELLED":
			case "LAST_REVOKED":
				return <Button
					text="Confirm"
					variant={ButtonVariant.DESTRUCTIVE}
					type={ButtonType.SOLID}
					round
					warning
					loading={loading}
					data-testid="cancel-request"
					onClick={() => {
						selectedData ? handleActionSubmit(selectedData) : handleActionAllSubmit()
					}}
					id="my-service-provider-modal-confirm-button"
				/>
		}
	};

	const renderMessage = (action, pitstops =[]) => {
		switch (action) {
			case "APPROVED":
				return (<div className="on-boarding__card-configure__body-content">
					{"You are about to approve this solution provider relationship."}
					<div>
						{"Do you want to proceed to approve?"}
					</div>
				</div>)
			case "REJECTED":
				return (
					<div className="on-boarding__card-configure__body-content">
						{"You are about to reject this solution provider relationship."}
						<div>
							{"Do you want to proceed to reject?"}
						</div>
					</div>
				)
			case "REVOKED":
				return (
					<div className="on-boarding__card-configure__body-content">
						{"You are about to revoke this solution provider relationship."}
						<div>
							{"Do you want to proceed to revoke?"}
						</div>
					</div>
				)
			case "CANCELLED":
				return (
					<div className="on-boarding__card-configure__body-content">
						{"You are about to cancel this solution provider relationship."}
						<div>
							{"Do you want to proceed to cancel?"}
						</div>
					</div>
				)
			case "LAST_CANCELLED":
				return (
					<div className="on-boarding__card-configure__body-content">
						{"By cancelling this relationship, you will revert to your default"}
						<div>
							{" solution provider for all data exchanges previously handled"}
						</div>
							{
								pitstops.length>1 ?(
									<div>

										{"by this provider. Please choose a pitstop to proceed."}
									</div>
								):(
									<div>

										{"by this provider. Do you want to proceed?"}
									</div>
								)
							}
					</div>
				)
			case "LAST_REVOKED":
				return (
					<div className="on-boarding__card-configure__body-content">
						{"By revoking this relationship, you will revert to your default"}
						<div>
							{" solution provider for all data exchanges previously handled"}
						</div>
							{
								pitstops.length>1 ?(
									<div>

										{"by this provider. Please choose a pitstop to proceed."}
									</div>
								):(
									<div>

										{"by this provider. Do you want to proceed?"}
									</div>
								)
							}
					</div>
				)


		}
	}

	// Custom Group Header Render Approve and Reject All
	const customGroupHeaderRender = (entries, utilities) => {
		const pendingEntries = entries.filter((entry) => (entry.serviceProviderRelationshipStatus.toUpperCase() === "PENDING" && entry.initiater === "SERVICE_PROVIDER" && entry.serviceProviderOrgId !== orgId));
		return (pendingEntries.length > 0) ? (<div className={`ds-multi-layer-table__group-header__right`}>
			<div className="ds-multi-layer-table__group__header__right__content">
				<div className="ds-multi-layer-table__group__header__right__content__reject" role="none" onClick={() => handleActionAll(pendingEntries, "REJECTED")} id="my-service-provider-reject-all-button">Reject All</div>
				<div className="ds-multi-layer-table__group__header__right__content__approve" role="none" onClick={() => handleActionAll(pendingEntries, "APPROVED")} id="my-service-provider-approve-all-button">Approve All</div>
			</div>
		</div>) : null;
	};

	return (
		<>
			<ServiceProviderTablePage
				title={title}
				description={description}
				groupBy={groupBy}
				data={serviceProviderData}
				headers={serviceProviderHeader}
				emptyMessage={emptyMessage}
				refreshData={refreshData}
				setRefreshData={setRefreshData}
				customGroupHeaderRender={customGroupHeaderRender}
				serviceProvidersItems={allOrgData.filter((org) => org.id != orgId).map((org) => { return { value: org.id, label: org.name , id: org.id} })}
				dataElementConsumeItems={dataElement.map((dataelement) => { return { value: dataelement.dataElementId, label: dataelement.elementName, id:dataelement.dataElementId } })}
				dataElementContributeItems={dataElement.map((dataelement) => { return { value: dataelement.dataElementId, label: dataelement.elementName , id: dataelement.dataElementId} })}
				serviceProviderLoading={serviceProviderLoading}
				dataElementLoading={dataElementLoading}
				orgDataLoading={orgDataLoading}
			/>

			{/* MODAL FOR DIFFERENT ACTIONS */}
			<Modal visible={initiateActionModal} close={hideActionModal} variant='medium' dismissClickOutsideModal={false}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										{selectedAction === statusEnum.APPROVED && <div className='card-configure__title__left__icon'>
											<IconDataExchange />
										</div>}
										{selectedAction !== statusEnum.APPROVED && <div className='card-configure__title__left__icon__reject'>
											<IconDataExchangeError />
										</div>}
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											<div className='card-configure__title__right__top__title'>
												Solution Provider Consent
											</div>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							{renderMessage(selectedAction, pitstops)}
							{
								pitstops?.length> 1 && (selectedAction==="LAST_CANCELLED" || selectedAction=== "LAST_REVOKED" )&&
								(
									<div className="card-configure__body__dropdown__option" data-testid="pitstop-modal">
										<span className="card-configure__body__dropdown__title">
											Pitstop
										</span>
										<InputDropdown
											placeholder={"Select your Pitstop(s) to host"}
											dropdownOption={pitstops.map((pitstop) => { return { value: pitstop.systemId, label: pitstop.systemName, id: pitstop.systemId } })}
											dropdownSelectedOption={selectPitstops}
											onSelectDropdownItem={pitstopDropdownSelector}
											multipleSelection={false}
											value={dropdownSearch.pitstopSearch}
											onChange={dropdownSearchHandler}
											name={'pitstopSearch'}
											isLoading={pitstopsLoading}
										/>
									</div>
								)
							}
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Left>
							<Button
								text="Cancel"
								variant={selectedAction === statusEnum.APPROVED ? ButtonVariant.STANDARD : ButtonVariant.DESTRUCTIVE}
								type={ButtonType.GHOST}
								data-testid="cancel"
								onClick={() => {
									hideActionModal();
								}}
								id="my-service-provider-modal-cancel-button"
							/>
						</CardConfigure.Footer.Left>
						<CardConfigure.Footer.Right>
							{renderButton(selectedAction)}
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Toast toastList={toastList} />

			<Portal id="app-blocker">
				{(loading) && (
					<div className="modal white">
						<div className="loading">
							<img src={assetUrl("/loading.gif")} alt="Loading" />
						</div>
					</div>
				)}
			</Portal>
		</>
	)
}

export default ServiceProviderPage;
